import { StravaTokenResponse, StravaActivity, StravaAthlete, RunningStats } from '../types/strava';

// @see https://developers.strava.com/docs/authentication/

const STRAVA_BASE_URL = 'https://www.strava.com/api/v3';
const STRAVA_AUTH_URL = 'https://www.strava.com/oauth/authorize';
const STRAVA_TOKEN_URL = 'https://www.strava.com/oauth/token';

const getStravaConfig = () => ({
  clientId: process.env.REACT_APP_STRAVA_CLIENT_ID || '',
  clientSecret: process.env.REACT_APP_STRAVA_CLIENT_SECRET || '',
  redirectUri: process.env.REACT_APP_STRAVA_REDIRECT_URI || '',
});

const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('strava_access_token');
};

const getAuthUrl = (): string => {
  const { clientId, redirectUri } = getStravaConfig();
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    approval_prompt: 'force',
    scope: 'read,activity:read_all',
  });

  return `${STRAVA_AUTH_URL}?${params.toString()}`;
};

const exchangeCodeForToken = async (code: string): Promise<StravaTokenResponse> => {
  const { clientId, clientSecret } = getStravaConfig();

  try {
    const response = await fetch(STRAVA_TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        client_secret: clientSecret,
        code: code,
        grant_type: 'authorization_code',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code for token');
    }

    const data: StravaTokenResponse = await response.json();

    localStorage.setItem('strava_access_token', data.access_token);
    localStorage.setItem('strava_refresh_token', data.refresh_token);
    localStorage.setItem('strava_expires_at', data.expires_at.toString());

    return data;
  } catch (error) {
    console.error('Error exchanging code for token:', error);
    throw error;
  }
};

const refreshAccessToken = async (): Promise<string> => {
  const { clientId, clientSecret } = getStravaConfig();
  const refreshToken = localStorage.getItem('strava_refresh_token');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await fetch(STRAVA_TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        client_secret: clientSecret,
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data: StravaTokenResponse = await response.json();

    localStorage.setItem('strava_access_token', data.access_token);
    localStorage.setItem('strava_refresh_token', data.refresh_token);
    localStorage.setItem('strava_expires_at', data.expires_at.toString());

    return data.access_token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
};

const getValidAccessToken = async (): Promise<string | null> => {
  const accessToken = localStorage.getItem('strava_access_token');
  const expiresAt = localStorage.getItem('strava_expires_at');

  if (!accessToken) {
    return null;
  }

  // Check if token is expired (with 5 minute buffer)
  const now = Math.floor(Date.now() / 1000);
  if (expiresAt && now >= parseInt(expiresAt) - 300) {
    try {
      return await refreshAccessToken();
    } catch (error) {
      clearTokens();
      return null;
    }
  }

  return accessToken;
};

const clearTokens = (): void => {
  localStorage.removeItem('strava_access_token');
  localStorage.removeItem('strava_refresh_token');
  localStorage.removeItem('strava_expires_at');
};

const fetchStrava = async <T = any>(endpoint: string): Promise<T> => {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    throw new Error('No valid access token');
  }

  const response = await fetch(`${STRAVA_BASE_URL}${endpoint}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status}`);
  }

  return response.json();
};

const loadYearActivities = async (year: number): Promise<StravaActivity[]> => {
  const startOfYear = new Date(year, 0, 1);
  const endOfYear = new Date(year + 1, 0, 1);

  const after = Math.floor(startOfYear.getTime() / 1000);
  const before = Math.floor(endOfYear.getTime() / 1000);

  try {
    // Strava API limits to 200 activities per request, so we might need pagination
    let allActivities: StravaActivity[] = [];
    let page = 1;
    const perPage = 200;

    while (true) {
      const activities = await fetchStrava<StravaActivity[]>(
        `/athlete/activities?after=${after}&before=${before}&page=${page}&per_page=${perPage}`
      );

      if (activities.length === 0) {
        break;
      }

      allActivities = allActivities.concat(activities);

      if (activities.length < perPage) {
        break;
      }

      page++;
    }

    return allActivities;
  } catch (error) {
    console.error('Error fetching activities:', error);
    throw error;
  }
};

const getRunningStats = async (year: number): Promise<RunningStats> => {
  try {
    const activities = await loadYearActivities(year);

    const runningActivities = activities.filter(
      (activity) => activity.type === 'Run' || activity.type === 'VirtualRun'
    );

    const totalDistance = runningActivities.reduce((sum, activity) => {
      return sum + (activity.distance || 0);
    }, 0);

    return {
      totalDistance: totalDistance / 1000,
      activities: runningActivities,
    };
  } catch (error) {
    console.error('Error calculating running stats:', error);
    throw error;
  }
};

const getAthleteInfo = async (): Promise<StravaAthlete> => {
  try {
    return await fetchStrava<StravaAthlete>('/athlete');
  } catch (error) {
    console.error('Error fetching athlete info:', error);
    throw error;
  }
};

export { clearTokens, exchangeCodeForToken, getAthleteInfo, getAuthUrl, getRunningStats, isAuthenticated };
